import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { CircleOfFifths } from "@/components/CircleOfFifths";
import { NextUp } from "@/components/Callouts";
import { circleIndex } from "@/theory/notes";

// Enharmonic-tolerant: idx("Bb") and idx("Bb") find the same seat.
const idx = circleIndex;

export default function MusicTheory11() {
  const { lesson, course, accent } = findLesson("music-theory", "11");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT11-Flat-Keys-Animated.mp4"
        caption="Flat key signatures — flats stack B E A D G C F"
        hint="≈ 32 sec · watch first"
      />
      <Chapter number="11" eyebrow="Music Theory · Chapter Eleven" title="The flat side of the circle">
        <Prose>
          <p>
            In the previous chapter we walked clockwise from C and watched the sharps pile
            up — one new sharp at every step, six steps in total before reaching the
            boundary at F♯. This chapter mirrors the same move in the opposite direction.
            We turn counter-clockwise from C, and instead of adding sharps we begin
            collecting <em>flats</em>. The rule is symmetrical, the math is the same;
            only the names change.
          </p>
          <p>
            Before we begin, one observation worth holding in your hand for the rest of
            theory: <strong>any two scales that are neighbours on the circle of fifths
            share six of their seven notes.</strong> They differ by exactly one note. C
            major and G major differ only in F vs F♯. Move to D major and the only new
            change is C vs C♯. Each clockwise (or counter-clockwise) step swaps a single
            note. That one-note-at-a-time movement is the whole reason the circle is so
            powerful — and it explains why every key signature builds on its neighbour by
            adding (or, on this side, replacing) exactly one accidental.
          </p>
        </Prose>
        <MTQuote>
          If you compare, let's say, C major and G major, the only difference between C
          major and G major scale is that F or F♯. So you have to argue C major has F, but G
          major does not have the F; instead it has an F♯.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="Same circle, opposite walk" title="Counter-clockwise from C, in fourths">
        <Prose>
          <p>
            Re-draw the circle if you need to. C at twelve, F at eleven, B♭ at ten, E♭ at
            nine, A♭ at eight, D♭ at seven, and G♭ at six (the same key as F♯, just spelt
            differently). Each step counter-clockwise is a perfect fourth up — or
            equivalently, a perfect fifth down. F is C's fourth. B♭ is F's fourth. E♭ is
            B♭'s fourth. The flat scales descend the circle in fourths, in lockstep with
            the flats they accumulate.
          </p>
        </Prose>

        <div className="mx-auto flex max-w-3xl justify-center py-4">
          <CircleOfFifths
            size={360}
            highlights={[
              { index: idx("C"),  color: "#94a3b8", label: "0" },
              { index: idx("F"),  color: "#a855f7", label: "1" },
              { index: idx("Bb"), color: "#a855f7", label: "2" },
              { index: idx("Eb"), color: "#a855f7", label: "3" },
              { index: idx("Ab"), color: "#a855f7", label: "4" },
              { index: idx("Db"), color: "#a855f7", label: "5" },
              { index: idx("F#"), color: "#a855f7", label: "6" },
            ]}
          />
          <p className="sr-only">
            The numbers on each highlighted segment show how many flats that key signature
            carries.
          </p>
        </div>

        <Prose>
          <p>
            Same C-to-F♯ diameter we drew before, only this time we walk it down the left
            side. Two questions then have clean answers: how do we know which flat to add
            at each step, and what do we call the resulting scale?
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The first flat" title="F major and its single B♭">
        <Prose>
          <p>
            Just as G major had to mug up its first sharp (F♯), F major has to mug up its
            first flat: <strong>B♭</strong>. F is C's perfect fourth, the first step
            counter-clockwise. Run the major-scale recipe from F and you'll see why — you
            need a half-step from the third to the fourth degree, which forces the B
            natural down to B♭.
          </p>
          <p>
            On the staff, that B♭ sits on the third (middle) line of the treble clef.
            One flat at the start of the line is enough to tell every player: "for the
            whole piece, every B you see is a B♭". So F major reads as F G A B♭ C D E F.
          </p>
          <p>
            A flat by definition <strong>removes the white key to its right</strong>.
            B♭ removes B. There is no B in F major; if you played B you'd be outside the
            scale. The flat sign, like the sharp, is essentially an instruction not to
            play the natural note next to it.
          </p>
        </Prose>

        <TheoryBox label="What flats and sharps actually do">
          A <strong>flat</strong> destroys the white key to its <em>right</em>. B♭ means
          "no B in this key — play the key just below B instead". A <strong>sharp</strong>
          destroys the white key to its <em>left</em>. F♯ means "no F — play the key just
          above F instead". Both signs are warnings, dressed up as note names.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The pattern" title="Each new flat is a fourth above the last">
        <Prose>
          <p>
            Here's where the flat side gets a beautiful trick — easier, in some ways,
            than the sharp side. Whatever flat just got added <em>becomes the next
            scale's tonic</em>. F major's only flat is B♭ — so the next scale around the
            circle is B♭ major. B♭ major's new flat is E♭ — so the next scale is E♭
            major. And so on, like a relay race where each scale hands its newest flat to
            the next runner.
          </p>
          <p>
            The new flat is always a perfect fourth above the previous flat (or
            equivalently, a fifth below it). B♭ is a fourth up from F. E♭ is a fourth up
            from B♭. A♭ is a fourth up from E♭. D♭ is a fourth up from A♭. G♭ is a fourth
            up from D♭. The same fifth-and-fourth geometry that drove the sharps drives
            the flats — just walked in the opposite direction.
          </p>
        </Prose>

        <TheoryBox label="The flat-side rule">
          Each counter-clockwise step around the circle adds exactly one new flat. The
          new flat is always a perfect fourth above the previous flat — and it is the
          name of the next scale you'll meet on the circle.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Walking the seven flat keys" title="From C to G♭ in single steps">
        <Steps>
          <Step><strong>C major</strong> — 0 flats. The starting point, shared with the sharp side.</Step>
          <Step><strong>F major</strong> — 1 flat: B♭. F is C's perfect fourth.</Step>
          <Step><strong>B♭ major</strong> — 2 flats: B♭, E♭. The new flat E♭ is a fourth above B♭.</Step>
          <Step><strong>E♭ major</strong> — 3 flats: B♭, E♭, A♭.</Step>
          <Step><strong>A♭ major</strong> — 4 flats: B♭, E♭, A♭, D♭.</Step>
          <Step><strong>D♭ major</strong> — 5 flats: B♭, E♭, A♭, D♭, G♭. Five black keys plus F and C as the only whites.</Step>
          <Step><strong>G♭ major</strong> — 6 flats: B♭, E♭, A♭, D♭, G♭, C♭. The same key as F♯ major; same six-flat boundary.</Step>
        </Steps>

        <Prose>
          <p>
            The order of flats — <strong>B♭, E♭, A♭, D♭, G♭, C♭, F♭</strong> — is exactly
            the order of sharps in reverse. Father Charles becomes Battle Ends. That
            symmetry is the signature beauty of the circle of fifths: one mnemonic,
            spoken forwards or backwards, covers every key on the staff.
          </p>
        </Prose>

        <TheoryBox label="The order of flats · mnemonic">
          <strong>B</strong>attle <strong>E</strong>nds <strong>A</strong>nd
          <strong> D</strong>own <strong>G</strong>oes <strong>C</strong>harles'
          <strong> F</strong>ather. Each word's first letter is the next flat in line —
          B, E, A, D, G, C, F.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The C♭ in G♭ major" title="The mirror image of E♯">
        <Prose>
          <p>
            The flat side has its own oddity, just like F♯ major had its E♯. G♭ major's
            sixth flat, by the alphabetical-fourth rule, has to land on the letter
            <strong> C</strong>. So we have to call it <strong>C♭</strong> — even though
            the key you actually press is plain old B.
          </p>
          <p>
            The reasoning is the same as the sharp side. G♭ major already uses G (as G♭),
            so we can't reuse G; the seven-letter rule says every letter from A to G must
            appear once. C must appear, and the recipe asks for it to be a half-step
            below D♭, which in pitch is the same key as B. So C♭ is a renaming, not a
            new black key.
          </p>
          <p>
            And G♭ major itself is the twin of F♯ major — same seven keys on the piano,
            two different spellings on the staff. Six sharps or six flats, depending on
            which side of the diameter you label it. That's why F♯ and G♭ are stacked at
            six o'clock as a single segment on the circle.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Three scales, five black keys" title="A useful way to remember the dark scales">
        <Prose>
          <p>
            Three of the major scales use exactly five black keys and two white keys.
            Once you know which three, you can spell them in seconds. They are:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>B major</strong> — five black keys, with <strong>B and E</strong> as the only white ones.</li>
            <li><strong>D♭ major</strong> (= C♯ major) — five black keys, with <strong>F and C</strong> as the only white ones.</li>
            <li><strong>G♭ major</strong> (= F♯ major) — five black keys, with <strong>B and F</strong> as the only white ones.</li>
          </ul>
          <p>
            The second of these is almost always written as D♭ (five flats) rather than
            C♯ (seven sharps). Same notes, but D♭ keeps the page cleaner. Knowing the
            "two white keys" rule lets you build any of these three scales without
            counting tones and semitones — you start on the tonic, fill in the five
            blacks, and drop in the two whites at the right alphabet positions.
          </p>
        </Prose>

        <Glossary>
          <Term name="Order of flats" def="B♭, E♭, A♭, D♭, G♭, C♭, F♭ — each new one a fourth above the last; the order of sharps spelled backwards." />
          <Term name="Enharmonic spelling" def="When the same key on the piano has two written names — e.g. F♯ and G♭, or E♯ and F." />
          <Term name="Six-flat boundary" def="G♭ major (= F♯ major) sits at six o'clock on the circle. Beyond it the spellings get unwieldy and we usually flip to the other twin." />
        </Glossary>

        <p>
          C major has zero, F major has one, B♭ has two, E♭ three, A♭ four, D♭ five, G♭ six.
          Once you can recite that, the staff stops surprising you.
        </p>
      </Chapter>

      <Chapter eyebrow="Putting it together" title="Twelve scales, one circle">
        <Prose>
          <p>
            Combining the previous chapter's sharp side with this chapter's flat side
            gives you all twelve major scales, each pinned to its position on the circle.
            Sharps climb in fifths from C; flats descend in fourths from C; F♯ and G♭
            meet at the bottom as a single key with two names. The whole geography of
            Western tonality, fitted onto one clock face.
          </p>
          <p>
            Practise drawing the circle from memory. Practise writing each scale with
            every letter A–G appearing once. Practise reciting the order of sharps and
            flats forward and backward. Once the geography is automatic, you stop
            thinking about key signatures and start thinking about music.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/11a-circle-of-fifths-flats-detail.png"
          caption="Jason's flat-side detail: ♭1 = F (1 flat), ♭2 = B♭ (2 flats), ♭3 = E♭ (3), ♭4 = A♭ (4), ♭5 = D♭ (5), ♭6 = G♭ (6), ♭7 = C♭ (7). Walk counter-clockwise on the circle and the flats accumulate one at a time — and they always come in the order B♭ E♭ A♭ D♭ G♭ C♭ F♭."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-12"
        label="Chapter 12 · How to remember Major Scales"
        why="With the key signatures mapped, the next move is to internalise the scales themselves — using shapes on the keyboard, the destroy-the-neighbour rule, and the seven-letter rule together."
      />
    </LessonShell>
  );
}
